'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import PageviewIcon from '@mui/icons-material/Pageview';
import DeleteIcon from '@mui/icons-material/Delete';
import getPORequestColumns from '../../utils/getPORequestColumns';
import useUserMap from '../../hooks/useUserMap';
import RfqNetsuitePreviewModal from './Modals/RfqNetsuitePreviewModal';
import ConfirmDialog from '../UserDialog/ConfirmDialog';
import { useNetSuiteProductionRfq } from '../../config/netsuite';
import { useAuth } from '../../context/AuthContext';
import { ProcurementAdminPermissions } from '../../utils/permissions';

import {
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
]);

const PendingApproval = ({ data, refetch }) => {
  const [rowData, setRowData] = useState([]);
  const userMap = useUserMap();
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [netsuitePreviewOpen, setNetsuitePreviewOpen] = useState(false);
  const [netsuitePreviewData, setNetsuitePreviewData] = useState([]);

  const [currentRfqId, setCurrentRfqId] = useState(null);

  const { userPermissions } = useAuth();

  const userPermissionNames = useMemo(
    () => userPermissions?.map(p => p.name) || [],
    [userPermissions]
  );

  const isProcurementAdmin = useMemo(
    () => userPermissionNames.some(p => ProcurementAdminPermissions.includes(p)),
    [userPermissionNames]
  );


  useEffect(() => {
    setRowData(data || []);
  }, [data]);

  const handleReviewClick = async (rfqRow) => {
    const rfqId = rfqRow.id;
    try {
      setCurrentRfqId(rfqId);

      const { data } = await axios.get(
        `/api/RfqHeader/rfq-grouped-po-preview?rfqId=${rfqId}&useProduction=${useNetSuiteProductionRfq}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setNetsuitePreviewData(data);
      setNetsuitePreviewOpen(true);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else if (err.response?.status === 400) {
        const errorMessage =
          typeof err.response?.data === 'string' ? err.response.data : 'Internal server error occurred.';
        toast.error(errorMessage);
      } else {
        const fallbackMessage =
          typeof err.response?.data === 'string' ? err.response.data : 'Failed to prepare PO preview.';
        toast.error(fallbackMessage);
      }
    }
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false });
  };

  const handleDeleteRfq = (rfqRow) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to archive "${rfqRow.name}"?`,
      onConfirm: async () => {
        try {
          await axios.put(`/api/RfqHeader/rfq-isdelete`, {
            id: rfqRow.id,
            isDeleted: true,
          });

          toast.success('Request archived successfully');
          await refetch();
        } catch (err) {
          console.error(err);
          if (err.response?.status === 403) {
            toast.error('Elevated permissions required for this action.');
          } else {
            toast.error('Archive failed.');
          }
        } finally {
          closeConfirmDialog();
        }
      },
      onCancel: closeConfirmDialog,
    });
  };

  const handleMarkRfqAsAwarded = async () => {
    try {
      if (!currentRfqId) {
        console.warn('Missing RFQ ID when trying to mark as awarded.');
        return;
      }

      await axios.put(`/api/RfqHeader/rfq-approval/${currentRfqId}`, {
        formStatus: true,
        isAwarded: true,
      });

      toast.success('RFQ status updated to Awarded.');
      await refetch();
      setCurrentRfqId(null);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('Failed to update RFQ status.');
      }
    }
  };

  const handleMarkRfqDenied = async () => {
    try {
      if (!currentRfqId) {
        console.warn('Missing RFQ ID when trying to mark as denied.');
        return;
      }

      await axios.put(`/api/RfqHeader/rfq-approval-needed`, {
        id: currentRfqId,
        readyForApproval: false,
      });

      toast.success('RFQ approval denied and moved back to Open Requests.');
      setCurrentRfqId(null);
      setNetsuitePreviewOpen(false); // 👈 closes the modal
      await refetch();
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Elevated permissions required for this action.');
      } else {
        toast.error('Failed to deny RFQ approval..');
      }
    }
  };

  const handlePreviewClose = () => {
    setNetsuitePreviewOpen(false);
    setNetsuitePreviewData([]);
  };

  const actionsRenderer = (params) => {
    const enabled = isProcurementAdmin;
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
        <Tooltip title={enabled ? 'Review Request' : 'Permission Denied'}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleReviewClick(params.data)}
              disabled={!isProcurementAdmin}
            >
              <PageviewIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={enabled ? 'Archive Request' : 'Permission Denied'}>
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteRfq(params.data)}
              disabled={!isProcurementAdmin}
            >
              <DeleteIcon fontSize="medium" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const columnDefs = useMemo(() => getPORequestColumns(userMap, actionsRenderer), [userMap]);

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: {
      textAlign: 'left',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        padding: 2,
      }}
    >
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <div style={{ height: '100%', width: '100%' }}>
          <AgGridReact columnDefs={columnDefs} defaultColDef={defaultColDef} rowData={rowData} pagination={true} />
        </div>
      </Box>

      <RfqNetsuitePreviewModal
        open={netsuitePreviewOpen}
        onClose={handlePreviewClose}
        netsuitePreviewData={netsuitePreviewData}
        onSubmissionComplete={handleMarkRfqAsAwarded}
        onSubmissionDenied={handleMarkRfqDenied}
        rfqId={currentRfqId}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
        onClose={closeConfirmDialog}
      />
    </Box>
  );
};

export default PendingApproval;
